import { AudioLines, type LucideIcon } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { waitingByThread } from '@/components/mobile-shell/activity-model';
import { AppConversationHost } from '@/features/apps/app-conversation';
import type { FeedNotification } from '@/features/notifications/notification-feed';
import { useNotifications } from '@/features/notifications/notifications';
import { CORE_LIVE_VIEW } from '@/features/plugins/core-panels';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useOpenLive } from '@/features/voice-call/live-panel';
import { openAppHome } from '@/lib/app-home';
import { dockApp } from '@/lib/desktop-shell/app-panels';
import { PREVIEW_APPS, PREVIEW_DEFAULT_PINS } from '@/lib/desktop-shell/preview-shell-data';
import type { SidebarWaiting } from '@/lib/desktop-shell/sidebar-model';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { useLibraryPage } from '@/lib/navigation';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import type { SidebarThread } from '@/lib/sidebar-threads';
import { recordAppOpened } from './sidebar-preferences';

/**
 * What the sidebar, the phone's drawer, ⌘K and the conversation menus act through: the shell
 * opens conversations, starts tasks and splits; they only say what the user chose.
 */
export type ShellNavigation = {
  preview: boolean;
  /** Every conversation that can be opened (the sidebar's, the assistants'). */
  threads: readonly SidebarThread[];
  activeThreadId: string;
  /** Opens a conversation of the list. */
  openThread: (thread: SidebarThread) => void;
  /** Opens a conversation by id (a project's own conversation, not listed). */
  openConversation: (conversationId: string) => void;
  /** A new conversation (⌘N, "New conversation"); there is no New conversation dialog. */
  newTask: () => void;
  /** Beside the one shown (⌥↵, "Open in split view"). */
  openSideBySide?: (thread: SidebarThread) => void;
  /** A row dragged to a pane's edge splits (D55); onto a project row, moves it. */
  startDrag?: (thread: SidebarThread, event: ReactPointerEvent<HTMLElement>) => void;
  /** Stops the agent of a conversation, when the shell can (the one shown). */
  stopAgent?: (thread: SidebarThread) => void;
  /** The existing handoff, for the cloud conversation shown. */
  continueLocally?: (thread: SidebarThread) => void;
  /** Renames, archives or deletes a conversation (D22). */
  renameConversation?: (thread: SidebarThread, title: string) => Promise<void>;
  archiveConversation?: (thread: SidebarThread) => Promise<void>;
  deleteConversation?: (thread: SidebarThread) => Promise<void>;
  /** Brings an archived conversation back to the sidebar. */
  unarchiveConversation?: (conversationId: string) => Promise<void>;
  /** Opens the dialog that adds a repository (a folder, a clone). */
  addRepository?: () => void;
};

const ShellNavigationContext = createContext<ShellNavigation | null>(null);

export function ShellNavigationProvider({
  value,
  children,
}: {
  value: ShellNavigation;
  children: ReactNode;
}) {
  // Pages over the shell (an app's home) offer its conversations too.
  useEffect(() => publishShellThreads(value.threads), [value.threads]);
  return (
    <ShellNavigationContext value={value}>
      {children}
      {/* The conversations apps start (their New …) are new tasks of the shell. */}
      <AppConversationHost newTask={value.newTask} />
    </ShellNavigationContext>
  );
}

/**
 * The shell's conversations, for the pages drawn over it outside its providers (an artifact's
 * page offers them as where its composer writes).
 */
let shellThreads: readonly SidebarThread[] = [];
const shellThreadListeners = new Set<() => void>();

function publishShellThreads(threads: readonly SidebarThread[]): void {
  if (threads === shellThreads) return;
  shellThreads = threads;
  for (const listener of shellThreadListeners) listener();
}

export function useShellThreads(): readonly SidebarThread[] {
  return useSyncExternalStore(
    (listener) => {
      shellThreadListeners.add(listener);
      return () => shellThreadListeners.delete(listener);
    },
    () => shellThreads,
    () => shellThreads,
  );
}

const NO_NAVIGATION: ShellNavigation = {
  preview: true,
  threads: [],
  activeThreadId: '',
  openThread: () => undefined,
  openConversation: () => undefined,
  newTask: () => undefined,
};

export function useShellNavigation(): ShellNavigation {
  return useContext(ShellNavigationContext) ?? NO_NAVIGATION;
}

/** An app the favourites and All apps offer. */
export type ShellApp = {
  view: string;
  title: string;
  description: string;
  icon: LucideIcon;
  iconSrc?: string | undefined;
  /** It cannot be opened (a disabled plugin). */
  disabled?: boolean;
  /** The Crowlingo application it brings: a "Crowlingo" badge with its origin dot. */
  service?: string | undefined;
};

/** The apps the user can open (the enabled plugins' pages), and how to open one. */
export function useShellApps(): {
  apps: readonly ShellApp[];
  defaultPins: readonly string[];
  openApp: (view: string) => void;
} {
  const { preview } = useShellNavigation();
  const workbench = useWorkbench();
  const store = useDesktopShellStoreApi();
  const openLive = useOpenLive();
  const { leave } = useLibraryPage();
  const panels = railPanels(workbench);
  const apps = useMemo<readonly ShellApp[]>(
    () =>
      preview
        ? PREVIEW_APPS
        : panels
            .filter((panel) => panel.unlisted !== true)
            .map((panel) => ({
              view: panel.view,
              title: panel.title,
              description: panel.description ?? '',
              icon: panel.view === CORE_LIVE_VIEW ? AudioLines : pluginIcon(panel.icon ?? 'app'),
              iconSrc: panel.iconSrc,
              ...(panel.service === undefined ? {} : { service: panel.service }),
            })),
    [panels, preview],
  );
  return {
    apps,
    defaultPins: preview ? PREVIEW_DEFAULT_PINS : [],
    openApp: (view) => {
      recordAppOpened(view);
      if (view.startsWith('preview:')) return;
      const panel = panels.find((candidate) => candidate.view === view);
      // Outside a conversation an app is its home, a full-screen page (§6.1).
      if (panel?.appView !== undefined || panel?.remoteView !== undefined)
        return openAppHome(panel.plugin);
      // A tool of the core (Live, the browser) is the conversation's: it docks beside it.
      leave();
      if (view === CORE_LIVE_VIEW) void openLive();
      else dockApp(store, view as SidePanelView);
    },
  };
}

const NO_WAITING_TYPES: ReadonlySet<string> = new Set();

/** What each conversation waits on the user for, from its notifications. */
function sidebarWaiting(
  notifications: readonly FeedNotification[],
  waitingTypes: ReadonlySet<string>,
): ReadonlyMap<string, SidebarWaiting> {
  const waiting = new Map<string, SidebarWaiting>();
  for (const [threadId, entry] of waitingByThread(notifications, waitingTypes))
    waiting.set(threadId, {
      kind: entry.kind,
      since: Date.parse(entry.notification.createdAt),
    });
  return waiting;
}

export function useSidebarWaiting(): ReadonlyMap<string, SidebarWaiting> {
  const notifications = useNotifications();
  const list = notifications?.notifications;
  const waitingTypes = notifications?.waitingTypes ?? NO_WAITING_TYPES;
  return useMemo(() => sidebarWaiting(list ?? [], waitingTypes), [list, waitingTypes]);
}
